export type LaunchActionName = "requestQuote" | "reviewQuote" | "approve" | "purchase" | "reconcile" | "uploadNext" |
  "closeExpired" | "discardDraft" | "prepareFunding" | "depositFunding" | "reconcileFunding" | "readFundingStatus" |
  "refundFunding" | "startAnother" | "selectAttempt" | "refreshRecovery";

export type WalletProgressCode =
  | "checking_recovery" | "reading_saved_launch" | "waiting_for_offer" | "saving_offer"
  | "checking_saved_offer" | "checking_chain_state" | "saving_review" | "checking_token_balance"
  | "simulating_transaction" | "checking_wallet_identity" | "checking_transaction_status"
  | "verifying_chain_evidence" | "saving_reconciled_status" | "checking_payment_evidence"
  | "checking_prompt_progress" | "checking_offer_expiry" | "checking_finalized_chain" | "closing_offer"
  | "checking_draft" | "discarding_draft" | "checking_launch_eligibility" | "saving_budget_choice"
  | "checking_funding_status" | "reading_funding_balance" | "verifying_funding_evidence"
  | "wallet_request_approval" | "wallet_request_purchase" | "wallet_request_prompt_upload"
  | "wallet_request_budget_approval" | "wallet_request_budget_deposit" | "wallet_request_budget_refund"
  | "transaction_submitted_approval" | "transaction_submitted_purchase" | "transaction_submitted_prompt_upload"
  | "transaction_submitted_budget_approval" | "transaction_submitted_budget_deposit" | "transaction_submitted_budget_refund"
  | "transaction_confirmed_approval" | "transaction_confirmed_purchase" | "prompt_progress_verified"
  | "transaction_confirmed_budget_approval" | "transaction_confirmed_budget_deposit" | "transaction_confirmed_budget_refund";

export type LaunchActivityStatus = "running" | "completed" | "submitted" | "confirmed" | "needs_check" | "failed" | "blocked";
export type LaunchActivityStepStatus = "complete" | "current" | "upcoming" | "submitted" | "confirmed" | "failed" | "unknown";
export type LaunchActivityStep = Readonly<{ id: string; label: string; status: LaunchActivityStepStatus }>;
export type LaunchActivitySnapshot = Readonly<{
  id: string;
  action: LaunchActionName;
  status: LaunchActivityStatus;
  visible: boolean;
  message: string | null;
  steps: readonly LaunchActivityStep[];
}>;

const ACTION_COPY: Readonly<Record<LaunchActionName, string>> = {
  requestQuote: "Request quote", reviewQuote: "Review quote", approve: "Approve USDC", purchase: "Pay and launch",
  reconcile: "Check transaction status", uploadNext: "Continue prompt upload", closeExpired: "Close expired offer",
  discardDraft: "Discard draft", prepareFunding: "Save starting budget choice", depositFunding: "Approve or deposit starting budget",
  reconcileFunding: "Reconcile starting budget", readFundingStatus: "Refresh starting budget status", refundFunding: "Return unspent budget",
  startAnother: "Start another launch", selectAttempt: "Open saved launch", refreshRecovery: "Check saved launch status",
};

const PHASE_LABELS: Readonly<Record<WalletProgressCode, string>> = {
  checking_recovery: "Checking previous launches and payments",
  reading_saved_launch: "Reading the selected saved launch",
  waiting_for_offer: "Waiting for a signed capacity offer",
  saving_offer: "Saving the signed offer",
  checking_saved_offer: "Checking the saved offer and wallet session",
  checking_chain_state: "Checking current chain state",
  saving_review: "Saving the offer review",
  checking_token_balance: "Checking USDC balance and allowance",
  simulating_transaction: "Checking the transaction before wallet handoff",
  checking_wallet_identity: "Checking the connected wallet",
  checking_transaction_status: "Checking the submitted transaction",
  verifying_chain_evidence: "Verifying canonical chain evidence",
  saving_reconciled_status: "Saving the verified transaction status",
  checking_payment_evidence: "Checking payment evidence",
  checking_prompt_progress: "Checking confirmed prompt progress",
  checking_offer_expiry: "Checking the offer deadline",
  checking_finalized_chain: "Checking finalized chain state",
  closing_offer: "Closing the expired offer",
  checking_draft: "Checking the saved draft",
  discarding_draft: "Discarding the saved draft",
  checking_launch_eligibility: "Checking launch and funding eligibility",
  saving_budget_choice: "Saving the selected budget amount",
  checking_funding_status: "Checking saved funding activity",
  reading_funding_balance: "Reading the verified vault balance",
  verifying_funding_evidence: "Verifying canonical funding evidence",
  wallet_request_approval: "Confirm the USDC approval in your wallet",
  wallet_request_purchase: "Confirm the payment in your wallet",
  wallet_request_prompt_upload: "Confirm the prompt upload in your wallet",
  wallet_request_budget_approval: "Confirm budget approval in your wallet",
  wallet_request_budget_deposit: "Confirm the budget deposit in your wallet",
  wallet_request_budget_refund: "Confirm the budget refund in your wallet",
  transaction_submitted_approval: "Approval transaction submitted; confirmation pending",
  transaction_submitted_purchase: "Payment transaction submitted; confirmation pending",
  transaction_submitted_prompt_upload: "Prompt upload submitted; confirmation pending",
  transaction_submitted_budget_approval: "Budget approval submitted; confirmation pending",
  transaction_submitted_budget_deposit: "Budget deposit submitted; confirmation pending",
  transaction_submitted_budget_refund: "Budget refund submitted; confirmation pending",
  transaction_confirmed_approval: "USDC approval confirmed on chain",
  transaction_confirmed_purchase: "Payment confirmed on chain",
  prompt_progress_verified: "Prompt progress verified on chain",
  transaction_confirmed_budget_approval: "Budget approval confirmed on chain",
  transaction_confirmed_budget_deposit: "Budget deposit confirmed on chain",
  transaction_confirmed_budget_refund: "Budget refund confirmed on chain",
};

export function createLaunchActivity(action: LaunchActionName, id: string): LaunchActivitySnapshot {
  return Object.freeze({ id, action, status: "running", visible: true, message: null,
    steps: Object.freeze([Object.freeze({ id: "command", label: `Command: ${ACTION_COPY[action]}`, status: "complete" as const })]) });
}

export function advanceLaunchActivity(activity: LaunchActivitySnapshot, code: WalletProgressCode): LaunchActivitySnapshot {
  if (activity.status !== "running") return activity;
  const currentIndex = activity.steps.findIndex((step) => step.status === "current");
  const foundIndex = activity.steps.findIndex((step) => step.id === code);
  const steps = [...activity.steps];
  let nextIndex = foundIndex;
  if (nextIndex < 0) {
    steps.push(Object.freeze({ id: code, label: PHASE_LABELS[code], status: "upcoming" }));
    nextIndex = steps.length - 1;
  }
  if (nextIndex < currentIndex) return activity;
  for (let index = 0; index < nextIndex; index++) if (steps[index]?.status === "current") {
    steps[index] = Object.freeze({ ...steps[index]!, status: "complete" });
  }
  if (steps[nextIndex]) {
    const status = code.startsWith("transaction_submitted_") ? "submitted" as const :
      code.startsWith("transaction_confirmed_") || code === "prompt_progress_verified" ? "confirmed" as const : "current" as const;
    steps[nextIndex] = Object.freeze({ ...steps[nextIndex]!, status });
  }
  return Object.freeze({ ...activity, steps: Object.freeze(steps) });
}

export function finishLaunchActivity(activity: LaunchActivitySnapshot, status: Exclude<LaunchActivityStatus, "running">, message: string | null): LaunchActivitySnapshot {
  if (activity.status !== "running") return activity;
  const terminalStep: LaunchActivityStepStatus = status === "submitted" ? "submitted" : status === "confirmed" ? "confirmed" :
    status === "failed" || status === "blocked" ? "failed" : status === "needs_check" ? "unknown" : "complete";
  const steps = activity.steps.map((step) => step.status === "current"
    ? Object.freeze({ ...step, status: terminalStep }) : step);
  return Object.freeze({ ...activity, status, message, steps: Object.freeze(steps) });
}

export function setLaunchActivityVisible(activity: LaunchActivitySnapshot, visible: boolean): LaunchActivitySnapshot {
  return Object.freeze({ ...activity, visible });
}

export function launchActivityCommand(action: LaunchActionName): string { return `You requested ${ACTION_COPY[action]}`; }
